import { useCallback, useMemo, useState } from 'react';
import { TRI_PAR_DEFAUT_VUE } from '@mosaik/core';
import { useReglages, useReleverTous, useSante, type FiltresCommandes } from './api/hooks';
import type { CanalEnvoi, CommandeListee, ResolutionHubspot } from './api/types';
import { useAuth } from './auth/AuthContext';
import { ChangerMotDePasse } from './auth/ChangerMotDePasse';
import { Connexion } from './auth/Connexion';
import { ArticlesCommande } from './composants/ArticlesCommande';
import { ChoixCommande } from './composants/ChoixCommande';
import { CommandeFournisseur } from './composants/CommandeFournisseur';
import { EnlevementHiolle } from './composants/EnlevementHiolle';
import { ConfirmerFactureNonReglee } from './composants/ConfirmerFactureNonReglee';
import { FicheCommande } from './composants/FicheCommande';
import { MailDepose } from './composants/MailDepose';
import { MailExpedition } from './composants/MailExpedition';
import { MailTracking } from './composants/MailTracking';
import { SaisieExpedition } from './composants/SaisieExpedition';
import { AjoutColis } from './composants/AjoutColis';
import { RelanceFournisseur } from './composants/RelanceFournisseur';
import { RelanceTransporteur } from './composants/RelanceTransporteur';
import { useToasts } from './composants/Toasts';
import { PageCommandes } from './pages/Commandes';
import { PageJournal } from './pages/Journal';
import { PageReglages } from './pages/Reglages';

type Page = 'commandes' | 'journal' | 'reglages';

const PAGES: Array<[Page, string]> = [
  ['commandes', 'Commandes'],
  ['journal', 'Journal'],
  ['reglages', 'Réglages'],
];

export function App() {
  const { moi, pret } = useAuth();

  if (!pret) {
    return (
      <div className="connexion">
        <p role="status"><span className="spin" /> Chargement…</p>
      </div>
    );
  }
  if (!moi) return <Connexion />;
  // Un mot de passe provisoire issu du seed doit être remplacé avant tout accès.
  if (moi.motDePasseAChanger) return <ChangerMotDePasse obligatoire />;
  return <Console />;
}

/** Flèches circulaires : le geste « recommencer », pas « envoyer ». */
const IconeRafraichir = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path
      d="M20 12a8 8 0 11-2.3-5.6M20 3.5V8h-4.5"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

/**
 * Actualise TOUT, à la demande : le portail fournisseur et les transporteurs.
 *
 * Posé à côté de l'horaire parce qu'il relève le même périmètre : la phrase dit
 * quand cela se produit tout seul, le bouton le provoque maintenant.
 *
 * **Il ne prépare aucun mail client.** Une date ou un tracking découvert chez
 * Fortemix inscrit la tâche « Prévenir le client » dans « À faire » — comme le
 * bouton « Relever chez Fortemix » d'une fiche, et comme le passage de 8 h 05
 * et 13 h 05. Le mail part ensuite de sa fenêtre de relecture : envoyé au clic,
 * sans brouillon, il ne doit jamais être composé sans que personne le lise.
 *
 * **Il est lent, et il le dit.** Le portail Fortemix est interrogé commande par
 * commande, puis les transporteurs en série, avec un Chromium à lancer :
 * comptez une à deux secondes par commande. Sans l'état « Actualisation en
 * cours… », un clic resterait sans effet visible pendant une minute — ce qui se
 * lit comme une panne, et fait cliquer trois fois.
 */
function ReleverTout() {
  const { ajouter } = useToasts();
  const releve = useReleverTous();

  const lancer = () => {
    releve.mutate(undefined, {
      onSuccess: ({ fortemix, suivi, paiement }) => {
        // Le compte rendu sépare les deux sources : elles n'apprennent pas la
        // même chose, et confondre leurs nombres empêcherait de voir laquelle
        // est muette. Ce qui n'a rien donné n'est pas énuméré — une phrase qui
        // dit « 0 » quatre fois ne se lit plus.
        const s = (n: number) => (n > 1 ? 's' : '');
        const morceaux: string[] = [];

        /*
         * Le règlement en tête du compte rendu.
         *
         * C'est lui qui débloque l'envoi au fournisseur : une facture qui vient
         * d'être payée se passe sans confirmation, et c'est la seule ligne du
         * bilan qui change ce qu'on a à faire dans la minute.
         *
         * Sellsy non configuré est dit une fois, sans être compté comme un
         * échec : ce n'est pas une panne, c'est une intégration absente.
         */
        if (paiement.indisponible) {
          morceaux.push('Règlement : Sellsy non configuré');
        } else if (paiement.regleesMaintenant > 0) {
          morceaux.push(
            `Règlement : ${paiement.regleesMaintenant} facture${s(paiement.regleesMaintenant)} `
            + `réglée${s(paiement.regleesMaintenant)} — commande${s(paiement.regleesMaintenant)} `
            + 'débloquée' + s(paiement.regleesMaintenant),
          );
        } else if (paiement.examinees > 0) {
          morceaux.push(
            `Règlement : rien de neuf sur ${paiement.examinees} facture${s(paiement.examinees)}`,
          );
        }

        const trouve = fortemix.datesTrouvees + fortemix.trackingsTrouves;
        if (trouve > 0) {
          const detail = [
            fortemix.datesTrouvees > 0 ? `${fortemix.datesTrouvees} date${s(fortemix.datesTrouvees)}` : null,
            fortemix.trackingsTrouves > 0 ? `${fortemix.trackingsTrouves} tracking${s(fortemix.trackingsTrouves)}` : null,
          ].filter(Boolean).join(' et ');
          // Aucun mail n'est parti : chaque découverte inscrit « Prévenir le
          // client ». Le dire évite de croire le client déjà prévenu.
          morceaux.push(
            `Fortemix : ${detail} — client${s(trouve)} à prévenir, voir « À faire »`,
          );
        }

        morceaux.push(
          suivi.misesAJour > 0
            ? `Suivi : ${suivi.misesAJour} mise${s(suivi.misesAJour)} à jour sur ${suivi.examinees}`
            : `Suivi : rien de neuf sur ${suivi.examinees} commande${s(suivi.examinees)}`,
        );

        const echecs = fortemix.echecs + suivi.echecs + paiement.erreurs.length;
        if (echecs > 0) morceaux.push(`${echecs} échec${s(echecs)}`);
        ajouter(`${morceaux.join(' · ')}.`);
      },
      onError: (e: unknown) => ajouter(
        `Actualisation interrompue : ${e instanceof Error ? e.message : String(e)}`,
      ),
    });
  };

  return (
    <button
      type="button"
      className="btn sm rafraichir"
      onClick={lancer}
      disabled={releve.isPending}
      // Le titre dit le périmètre : le bouton ne relève pas « tout », il relève
      // ce qui est encore en route.
      title="Relever le règlement des factures, le portail Fortemix, puis le suivi de toutes les commandes non livrées"
      aria-label="Actualiser le règlement des factures, le portail fournisseur et le suivi transporteur de toutes les commandes en cours"
    >
      <span className={releve.isPending ? 'tourne' : undefined}><IconeRafraichir /></span>
      {releve.isPending ? 'Actualisation en cours…' : 'Actualiser'}
    </button>
  );
}

function Console() {
  const { moi, deconnecter } = useAuth();
  const { ajouter } = useToasts();
  const { data: reglages } = useReglages();
  const { data: sante, error: erreurSante } = useSante();

  const [page, setPage] = useState<Page>('commandes');
  const [filtres, setFiltresBrut] = useState<FiltresCommandes>({
    // « À faire » à l'ouverture : c'est la question qu'on se pose en arrivant.
    // Depuis que les deux onglets se partagent les commandes non livrées, « En
    // cours » ne contient plus que ce qui n'attend rien de nous — utile à
    // consulter, mais pas ce par quoi on commence sa journée.
    //
    // Le tri suit l'onglet : les urgences par gravité, comme partout ailleurs.
    vue: 'afaire', tri: TRI_PAR_DEFAUT_VUE.afaire, page: 1, taille: 50,
  });
  const [selection, setSelection] = useState<string | null>(null);
  const [relance, setRelance] = useState<CommandeListee | null>(null);
  const [relanceFrs, setRelanceFrs] = useState<CommandeListee | null>(null);
  /** La demande d'enlèvement DHL, ouverte depuis la liste comme depuis la fiche. */
  const [enlevement, setEnlevement] = useState<CommandeListee | null>(null);
  const [commandeFrs, setCommandeFrs] = useState<CommandeListee | null>(null);
  const [articles, setArticles] = useState<CommandeListee | null>(null);
  const [choix, setChoix] = useState<{ commande: CommandeListee; motif: string } | null>(null);
  const [impaye, setImpaye] = useState<
    { commande: CommandeListee; raison: string; continuer: () => void } | null
  >(null);
  /** Le mail au client à relire, ouvert depuis la liste comme depuis la fiche. */
  const [mailClient, setMailClient] = useState<
    { commande: CommandeListee; canal: 'expedition' | 'tracking'; colis?: string } | null
  >(null);
  /** Un colis de plus, pour une commande partie en plusieurs envois. */
  const [ajoutColis, setAjoutColis] = useState<CommandeListee | null>(null);
  /** La saisie d'une date d'expédition ou d'un tracking, suivie de son mail. */
  const [saisie, setSaisie] = useState<
    { commande: CommandeListee; champ: 'expedition' | 'tracking' } | null
  >(null);
  /** Le mail déjà envoyé pour un envoi, relu dans Outlook. */
  const [mailVu, setMailVu] = useState<{ commande: CommandeListee; canal: CanalEnvoi } | null>(null);
  const [doublons, setDoublons] = useState<Extract<ResolutionHubspot, { statut: 'doublons' }> | null>(null);
  const [changementMdp, setChangementMdp] = useState(false);
  const [lienBloque, setLienBloque] = useState<{ url: string; client: string } | null>(null);

  /**
   * Les fenêtres que les actions ouvrent, rassemblées une fois.
   *
   * La liste et la fiche les reçoivent toutes les deux : cliquer une action dans
   * la dernière colonne doit ouvrir exactement la même fenêtre que cliquer son
   * bouton dans la fiche.
   */
  const modales = useMemo(() => ({
    onRelanceFournisseur: setRelanceFrs,
    onRelanceTransporteur: setRelance,
    onCommandeARelire: setCommandeFrs,
    onChoixCommande: (commande: CommandeListee, motif: string) => setChoix({ commande, motif }),
    onArticles: setArticles,
    onFactureNonReglee: (commande: CommandeListee, raison: string, continuer: () => void) =>
      setImpaye({ commande, raison, continuer }),
    onOuvrirFiche: (commande: CommandeListee) => setSelection(commande.id),
    onEnlevement: setEnlevement,
    onMailClient: (commande: CommandeListee, canal: 'expedition' | 'tracking', colis?: string) =>
      setMailClient({ commande, canal, colis }),
    onAjoutColis: setAjoutColis,
    onSaisie: (commande: CommandeListee, champ: 'expedition' | 'tracking') =>
      setSaisie({ commande, champ }),
    onVoirMail: (commande: CommandeListee, canal: CanalEnvoi) => setMailVu({ commande, canal }),
  }), []);

  const setFiltres = useCallback((partiel: Partial<FiltresCommandes>) => {
    // Toute modification de filtre ramène en page 1, sauf changement de page.
    setFiltresBrut((f) => ({ ...f, ...partiel, ...(partiel.page === undefined ? { page: 1 } : {}) }));
  }, []);

  if (changementMdp) return <ChangerMotDePasse onAnnuler={() => setChangementMdp(false)} />;

  return (
    <div className="app">
      <a className="saut" href="#contenu">Aller au contenu</a>

      <aside className="side">
        <div className="brand">
          <div className="mark" aria-hidden="true"><i /><i /><i /><i /></div>
          <div>
            <b>Mosaik Floor</b>
            <span>Flux commandes</span>
          </div>
        </div>

        <nav className="nav" aria-label="Navigation principale">
          {PAGES.map(([cle, libelle]) => (
            <button
              key={cle} type="button"
              aria-current={page === cle ? 'page' : undefined}
              onClick={() => setPage(cle)}
            >
              {libelle}
            </button>
          ))}
        </nav>

        <footer>
          Connecté : {moi?.nom ?? moi?.email}
          <br />
          <button type="button" onClick={() => setChangementMdp(true)}>
            Changer de mot de passe
          </button>
          <br />
          <button
            type="button"
            onClick={() => { void deconnecter().then(() => ajouter('Déconnecté.')); }}
          >
            Se déconnecter
          </button>
        </footer>
      </aside>

      <div className="main">
        <div className="topbar">
          <div className="search">
            <label htmlFor="q" style={{ position: 'absolute', left: -9999 }}>Rechercher</label>
            <input
              id="q" type="search" placeholder="Client, n° de commande, ville, tracking…"
              value={filtres.q ?? ''}
              onChange={(e) => setFiltres({ q: e.target.value })}
            />
          </div>
          {/* L'horaire vient du serveur, pas d'un champ de réglages : il annonçait
              « 07:00 et 19:00 » alors qu'aucun relevé n'existait. */}
          <div className="sync">
            {sante?.suivi && (
              <span>
                {sante.suivi.automatique
                  ? `Suivi transporteur relevé à ${sante.suivi.heures
                      .map((h) => `${String(h).padStart(2, '0')}:00`).join(' et ')}`
                  : 'Relevé automatique du suivi désactivé'}
              </span>
            )}
            <ReleverTout />
          </div>
        </div>

        {/* Un problème d'infrastructure se dit clairement, en haut, sans masquer l'outil. */}
        {erreurSante && (
          <div className="bandeau mauvais" role="alert">
            L&apos;API ne répond pas. Les données affichées peuvent être périmées et les
            enregistrements échoueront.
          </div>
        )}
        {sante?.base.etat === 'reveil' && (
          <div className="bandeau avert" role="status">
            La base sort de veille : les premières requêtes seront lentes. {sante.base.message}
          </div>
        )}
        {sante?.base.etat === 'injoignable' && (
          <div className="bandeau mauvais" role="alert">{sante.base.message}</div>
        )}
        {/* Boîte d'envoi mal configurée : aucun mail ne part, et ce n'est
            pas une panne d'Outlook — il faut le dire, sinon on cherche ailleurs. */}
        {sante?.brouillons.boiteRefusee && (
          <div className="bandeau mauvais" role="alert">
            <strong>Aucun mail ne peut être envoyé.</strong> {sante.brouillons.boiteRefusee}
          </div>
        )}
        {sante?.integrations.outlook.simule && (
          <div className="bandeau avert" role="status">
            Outlook en mode local : les mails sont composés, mais aucun n&apos;est envoyé.
          </div>
        )}

        {page === 'commandes' && (
          <PageCommandes
            filtres={filtres} setFiltres={setFiltres}
            selection={selection} onSelection={setSelection}
            reglages={reglages}
            hubspot={sante?.integrations.hubspot}
            onDoublons={setDoublons}
            onLienBloque={(url, client) => setLienBloque({ url, client })}
            modales={modales}
          />
        )}
        {page === 'journal' && <PageJournal />}
        {page === 'reglages' && <PageReglages />}
      </div>

      {selection && (
        <FicheCommande
          id={selection} reglages={reglages}
          onFermer={() => setSelection(null)}
          modales={modales}
        />
      )}

      {relance && (
        <RelanceTransporteur
          commande={relance} reglages={reglages} onFermer={() => setRelance(null)}
        />
      )}

      {relanceFrs && (
        <RelanceFournisseur
          commande={relanceFrs} reglages={reglages} onFermer={() => setRelanceFrs(null)}
        />
      )}

      {enlevement && <EnlevementHiolle commande={enlevement} onFermer={() => setEnlevement(null)} />}

      {/* La saisie d'abord : elle cède la place au mail qu'elle prépare, ou au
          mail déjà envoyé qu'on demande à relire. */}
      {saisie && (
        <SaisieExpedition
          commande={saisie.commande} reglages={reglages} champ={saisie.champ}
          onFermer={() => setSaisie(null)}
          onVoirMail={() => { setMailVu({ commande: saisie.commande, canal: saisie.champ }); setSaisie(null); }}
          onPreparerMail={(maj) => { setMailClient({ commande: maj, canal: saisie.champ }); setSaisie(null); }}
          onAjouterColis={() => { setAjoutColis(saisie.commande); setSaisie(null); }}
        />
      )}

      {ajoutColis && (
        <AjoutColis
          commande={ajoutColis} reglages={reglages}
          onFermer={() => setAjoutColis(null)}
          onPreparerMail={(maj, colis) => { setMailClient({ commande: maj, canal: 'tracking', colis }); setAjoutColis(null); }}
        />
      )}

      {mailVu && (
        <MailDepose
          commande={mailVu.commande} canal={mailVu.canal}
          onFermer={() => setMailVu(null)}
          // Refaire n'a de sens que pour les mails au client : repasser une
          // commande au fournisseur a son propre chemin, l'action de la liste.
          {...(mailVu.canal !== 'fournisseur' && {
            libelleRefaire: 'Refaire le mail au client',
            onRefaire: () => {
              setMailClient({ commande: mailVu.commande, canal: mailVu.canal as 'expedition' | 'tracking' });
              setMailVu(null);
            },
          })}
        />
      )}

      {mailClient?.canal === 'expedition' && (
        <MailExpedition commande={mailClient.commande} onFermer={() => setMailClient(null)} />
      )}
      {mailClient?.canal === 'tracking' && (
        <MailTracking
          commande={mailClient.commande} colis={mailClient.colis} onFermer={() => setMailClient(null)}
        />
      )}

      {/* Avant la préparation du mail, jamais à sa place : confirmer ferme
          cette fenêtre et ouvre la suite ordinaire — carrefour ou relecture. */}
      {impaye && (
        <ConfirmerFactureNonReglee
          commande={impaye.commande} raison={impaye.raison}
          onConfirmer={() => { const { continuer } = impaye; setImpaye(null); continuer(); }}
          onFermer={() => setImpaye(null)}
        />
      )}

      {commandeFrs && (
        <CommandeFournisseur
          commande={commandeFrs} reglages={reglages} onFermer={() => setCommandeFrs(null)}
        />
      )}

      {/* Le carrefour : corriger les articles, ou écrire le mail. Il se ferme
          en ouvrant l'une des deux fenêtres, jamais les deux. */}
      {choix && (
        <ChoixCommande
          commande={choix.commande} motif={choix.motif}
          onArticles={() => { setArticles(choix.commande); setChoix(null); }}
          onMail={() => { setCommandeFrs(choix.commande); setChoix(null); }}
          onFermer={() => setChoix(null)}
        />
      )}

      {articles && (
        <ArticlesCommande
          commande={articles} onFermer={() => setArticles(null)}
        />
      )}

      {doublons && <ChoixDoublons resolution={doublons} onFermer={() => setDoublons(null)} />}

      {lienBloque && (
        <LienBloque
          url={lienBloque.url} client={lienBloque.client} onFermer={() => setLienBloque(null)}
        />
      )}
    </div>
  );
}

/**
 * Le navigateur a bloqué la fenêtre. La fiche a bien été trouvée : on donne le
 * lien plutôt que de laisser l'utilisateur devant un clic sans effet.
 */
function LienBloque({
  url, client, onFermer,
}: { url: string; client: string; onFermer: () => void }) {
  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label="Fiche HubSpot trouvée">
        <div className="mhead">
          <h2>Fiche HubSpot trouvée</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>
        <div className="mbody">
          <p style={{ marginBottom: 14 }}>
            Votre navigateur a bloqué l&apos;ouverture automatique. La fiche de {client} existe
            bien — ouvrez-la depuis ce lien. Autorisez les fenêtres pour ce site afin que le
            prochain clic fonctionne directement.
          </p>
          <a
            className="btn pri" href={url} target="_blank" rel="noopener noreferrer"
            style={{ textDecoration: 'none' }} onClick={onFermer}
          >
            Ouvrir la fiche dans HubSpot
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Doublons HubSpot : plusieurs contacts partagent le même email. On laisse
 * choisir plutôt que de deviner lequel ouvrir.
 */
function ChoixDoublons({
  resolution, onFermer,
}: {
  resolution: Extract<ResolutionHubspot, { statut: 'doublons' }>;
  onFermer: () => void;
}) {
  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label="Doublons HubSpot" style={{ borderTopColor: 'var(--jaune)' }}>
        <div className="mhead">
          <h2>Plusieurs contacts HubSpot</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>
        <div className="mbody">
          <p style={{ marginBottom: 14 }}>
            {resolution.contacts.length} contacts partagent cette adresse. Choisissez la fiche à ouvrir —
            et pensez à fusionner les doublons dans HubSpot.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {resolution.contacts.map((c) => (
              <li key={c.id} style={{ borderBottom: '1px solid var(--line)', padding: '10px 0' }}>
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="btn sm" style={{ textDecoration: 'none' }}>
                  {[c.prenom, c.nom].filter(Boolean).join(' ') || c.email} — ouvrir
                </a>
                <div className="sub">{c.email} · id {c.id}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
